import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { shipmentsAPI, uploadAPI } from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import AddressDropdown from '../components/AddressDropdown';
import RejectionBanner from '../components/RejectionBanner';
import './NewShipment.css';

function NewShipment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [shipperSuggestions, setShipperSuggestions] = useState([]);
  const [consigneeSuggestions, setConsigneeSuggestions] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editShipmentId, setEditShipmentId] = useState(null);
  const [editRejectionData, setEditRejectionData] = useState(null);

  const [formData, setFormData] = useState({
    shipment_type: 'domestic',
    // Shipper
    shipper_name: '',
    shipper_address: '',
    shipper_address2: '',
    shipper_city: '',
    shipper_state: '',
    shipper_zip: '',
    shipper_country: 'US',
    shipper_contact: '',
    shipper_phone: '',
    shipper_email: '',
    shipper_reference_num: '',
    shipper_ipt_acp_num: '',
    shipper_ncr_num: '',
    purchase_order: '',
    production_order: '',
    charge_code: '',
    ots: false,
    wbs_number: '',
    // Consignee
    consignee_name: '',
    consignee_address: '',
    consignee_address2: '',
    consignee_city: '',
    consignee_state: '',
    consignee_zip: '',
    consignee_country: 'US',
    consignee_contact: '',
    consignee_phone: '',
    consignee_email: '',
    consignee_reference_num: '',
    // Pickup/Delivery
    pickup_date: '',
    pickup_window_start: '08:59',
    pickup_window_end: '17:59',
    delivery_date: '',
    delivery_window_start: '08:59',
    delivery_window_end: '17:59',
    // Billing/Shipping
    bill_to: 'inpayer',
    service: '',
    expedite: '',
    first_approval: '',
    second_approval: '',
    active_outage: '',
    // Accessorials
    acc_liftgate_air: false,
    acc_liftgate_gr: false,
    acc_inside_air: false,
    acc_inside_gr: false,
    acc_rigging_air: false,
    acc_rigging_gr: false,
    acc_construction_air: false,
    acc_construction_gr: false,
    acc_tarp_needed: false,
    acc_protect_freezing: false,
    // Other
    product_location: '',
    special_instructions: '',
  });

  const [lineItems, setLineItems] = useState([]);

  // Handle cloned shipment data
  useEffect(() => {
    if (location.state?.cloneData) {
      const cloneData = location.state.cloneData;
      // Normalize boolean fields — MySQL returns 0/1 integers, React needs true/false
      const booleanFields = [
        'ots', 'acc_liftgate_air', 'acc_liftgate_gr', 'acc_inside_air', 'acc_inside_gr',
        'acc_rigging_air', 'acc_rigging_gr', 'acc_construction_air', 'acc_construction_gr',
        'acc_tarp_needed', 'acc_protect_freezing'
      ];
      const normalizedCloneData = { ...cloneData };
      booleanFields.forEach(field => {
        if (field in normalizedCloneData) {
          normalizedCloneData[field] = !!normalizedCloneData[field];
        }
      });
      setFormData((prev) => ({
        ...prev,
        ...normalizedCloneData,
        // Clear ID and dates for new shipment
        id: undefined,
        request_date: undefined,
        created_at: undefined,
        updated_at: undefined,
      }));

      // Show message that data was cloned
      setMessage({
        type: 'success',
        text: 'Shipment data cloned successfully. Review and submit to create a new shipment.',
      });

      // Scroll to top to show message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.state]);

  // Handle edit mode (resubmit rejected shipment)
  useEffect(() => {
    if (location.state?.editMode && location.state?.editData) {
      const editData = location.state.editData;
      setEditMode(true);
      setEditShipmentId(editData.id);
      setEditRejectionData({
        rejection_reason: editData.rejection_reason,
        rejected_by_name: editData.rejected_by_name,
        rejected_at: editData.rejected_at,
      });

      setFormData((prev) => ({
        ...prev,
        ...editData,
      }));

      // Pre-populate line items if available
      if (editData.line_items && editData.line_items.length > 0) {
        setLineItems(editData.line_items);
      }

      setMessage({
        type: 'info',
        text: 'Edit your shipment and submit to resubmit for approval.',
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (location.state?.editMode && !location.state?.editData) {
      // Missing edit data, redirect to dashboard
      navigate('/dashboard');
    }
  }, [location.state, navigate]);

  // Fetch previous shipper/consignee names for autocomplete
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await shipmentsAPI.getAll();
        const shipments = response.data.shipments || [];

        // Get unique shipper names
        const shippers = [...new Set(shipments.map((s) => s.shipper_name).filter((name) => name))];

        // Get unique consignee names
        const consignees = [
          ...new Set(shipments.map((s) => s.consignee_name).filter((name) => name)),
        ];

        setShipperSuggestions(shippers);
        setConsigneeSuggestions(consignees);
      } catch (err) {
        console.error('Failed to fetch suggestions:', err);
      }
    };

    fetchSuggestions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleShipperSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      shipper_name: address.name || '',
      shipper_address: address.address || '',
      shipper_address2: address.address2 || '',
      shipper_city: address.city || '',
      shipper_state: address.state || '',
      shipper_zip: address.zip || '',
      shipper_country: address.country || 'US',
      shipper_contact: address.contact || '',
      shipper_phone: address.phone || '',
      shipper_email: address.email || '',
    }));
  };

  const handleConsigneeSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      consignee_name: address.name || '',
      consignee_address: address.address || '',
      consignee_address2: address.address2 || '',
      consignee_city: address.city || '',
      consignee_state: address.state || '',
      consignee_zip: address.zip || '',
      consignee_country: address.country || 'US',
      consignee_contact: address.contact || '',
      consignee_phone: address.phone || '',
      consignee_email: address.email || '',
    }));
  };

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      {
        pieces: '',
        handling_units: '',
        type: '',
        weight_lbs: '',
        length_in: '',
        width_in: '',
        height_in: '',
        description: '',
        class: '',
        locked: false,
      },
    ]);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const lockLineItem = (index) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], locked: true };
      return updated;
    });
  };

  const unlockLineItem = (index) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], locked: false };
      return updated;
    });
  };

  const removeLineItem = (index) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearForm = () => {
    if (window.confirm('Are you sure you want to clear the form?')) {
      window.location.reload();
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    // Combine with existing files
    const combined = [...selectedFiles, ...newFiles];
    
    // Validate max 10 files total
    if (combined.length > 10) {
      setMessage({
        type: 'error',
        text: `Maximum 10 files allowed. You already have ${selectedFiles.length} selected.`,
      });
      e.target.value = '';
      return;
    }
    
    // Validate each new file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const invalidFiles = newFiles.filter(file => file.size > maxSize);
    if (invalidFiles.length > 0) {
      setMessage({
        type: 'error',
        text: `File(s) too large: ${invalidFiles.map(f => f.name).join(', ')}. Max size is 10MB per file.`,
      });
      e.target.value = '';
      return;
    }
    
    setSelectedFiles(combined);
    e.target.value = ''; // Reset input so same file can be added again
  };

  const validateForm = () => {
    const errors = [];

    // Shipper - Required fields
    if (!formData.shipper_name?.trim()) errors.push('Shipper Name is required');
    if (!formData.shipper_address?.trim()) errors.push('Shipper Address is required');
    if (!formData.shipper_city?.trim()) errors.push('Shipper City is required');
    if (!formData.shipper_state?.trim()) errors.push('Shipper State is required');
    if (!formData.shipper_zip?.trim()) errors.push('Shipper ZIP is required');
    if (!formData.shipper_country?.trim()) errors.push('Shipper Country is required');
    if (!formData.shipper_contact?.trim()) errors.push('Shipper Contact is required');
    if (!formData.shipper_phone?.trim()) errors.push('Shipper Phone is required');
    if (!formData.shipper_email?.trim()) errors.push('Shipper Email is required');
    if (!formData.shipper_reference_num?.trim()) errors.push('Shipper Reference Number is required');
    
    // Purchase Order and Production Order
    if (!formData.purchase_order?.trim()) errors.push('Purchase Order is required (enter PO # or N/A)');
    if (!formData.production_order?.trim()) errors.push('Production Order is required (enter Prod # or NA)');
    if (!formData.charge_code?.trim()) errors.push('Charge Code is required');

    // Consignee - Required fields
    if (!formData.consignee_name?.trim()) errors.push('Consignee Name is required');
    if (!formData.consignee_address?.trim()) errors.push('Consignee Address is required');
    if (!formData.consignee_city?.trim()) errors.push('Consignee City is required');
    if (!formData.consignee_state?.trim()) errors.push('Consignee State is required');
    if (!formData.consignee_zip?.trim()) errors.push('Consignee ZIP is required');
    if (!formData.consignee_country?.trim()) errors.push('Consignee Country is required');
    if (!formData.consignee_contact?.trim()) errors.push('Consignee Contact is required');
    if (!formData.consignee_phone?.trim()) errors.push('Consignee Phone is required');
    if (!formData.consignee_email?.trim()) errors.push('Consignee Email is required');
    if (!formData.consignee_reference_num?.trim()) errors.push('Consignee Reference Number is required');

    // Pickup/Delivery
    if (!formData.pickup_date) errors.push('Pickup Date is required');
    if (!formData.delivery_date) errors.push('Delivery Date is required');
    if (!formData.pickup_window_start) errors.push('Pickup Window Start is required');
    if (!formData.pickup_window_end) errors.push('Pickup Window End is required');
    if (!formData.delivery_window_start) errors.push('Delivery Window Start is required');
    if (!formData.delivery_window_end) errors.push('Delivery Window End is required');

    // Billing and Service
    if (!formData.bill_to) errors.push('Bill To is required (select one option)');
    if (!formData.service?.trim()) errors.push('Shipping Service is required');
    if (!formData.active_outage) errors.push('Active Outage selection is required');
    
    if (!formData.product_location) errors.push('Product Location is required');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.shipper_email && !emailRegex.test(formData.shipper_email)) {
      errors.push('Shipper Email format is invalid');
    }
    if (formData.consignee_email && !emailRegex.test(formData.consignee_email)) {
      errors.push('Consignee Email format is invalid');
    }

    // Phone validation (basic)
    const phoneRegex = /^[\d\s\-\(\)\.+]{10,}$/;
    if (formData.shipper_phone && !phoneRegex.test(formData.shipper_phone)) {
      errors.push('Shipper Phone format is invalid');
    }
    if (formData.consignee_phone && !phoneRegex.test(formData.consignee_phone)) {
      errors.push('Consignee Phone format is invalid');
    }

    // Date validation
    if (formData.pickup_date && formData.delivery_date) {
      const pickup = new Date(formData.pickup_date);
      const delivery = new Date(formData.delivery_date);
      if (delivery < pickup) {
        errors.push('Delivery Date cannot be before Pickup Date');
      }
    }

    // Expedite validation
    if (formData.service === 'Expedite') {
      if (!formData.expedite) {
        errors.push('Expedite option is required when Service is Expedite');
      }
      if (!formData.first_approval || !formData.second_approval) {
        errors.push('Expedited shipments must have 2 approvals');
      }
    }

    // OTS validation
    if (formData.ots && !formData.wbs_number?.trim()) {
      errors.push('WBS # is required when OTS is checked');
    }

    // Line Items validation - at least one line item with pieces is required
    const validLineItems = lineItems.filter((item) => 
      item.pieces || item.handling_units || item.type || 
      item.weight_lbs || item.length_in || item.width_in || item.height_in || 
      item.description || item.class
    );
    
    if (validLineItems.length === 0) {
      errors.push('At least one Line Item is required. Please add package details (Pieces, Type, Weight, Dimensions, etc.)');
    }
    
    // Validate that line items with data have required fields
    validLineItems.forEach((item, index) => {
      if (!item.pieces && !item.handling_units) {
        errors.push(`Line Item ${index + 1}: Pieces (PCS) is required`);
      }
      if (!item.type) {
        errors.push(`Line Item ${index + 1}: Type is required`);
      }
      if (!item.weight_lbs) {
        errors.push(`Line Item ${index + 1}: Weight is required`);
      }
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setMessage({
        type: 'error',
        text: validationErrors.join('; '),
      });
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const submitData = {
        ...formData,
        lineItems: lineItems.filter((item) => 
          // Keep line item if ANY field has a value
          item.pieces || item.handling_units || item.type || 
          item.weight_lbs || item.length_in || item.width_in || item.height_in || 
          item.description || item.class
        ),
      };

      // Edit mode: call update (resubmit)
      if (editMode && editShipmentId) {
        try {
          await shipmentsAPI.update(editShipmentId, submitData);
          setMessage({
            type: 'success',
            text: 'Shipment resubmitted successfully! Redirecting to My Shipments...',
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } catch (err) {
          if (err.response?.status === 409) {
            setMessage({
              type: 'error',
              text: 'This shipment can no longer be edited in its current state.',
            });
          } else if (err.response?.status === 500) {
            setMessage({
              type: 'error',
              text: 'Resubmit failed — admin notification could not be sent. Please try again.',
            });
          } else {
            setMessage({
              type: 'error',
              text: err.response?.data?.message || 'Failed to resubmit shipment',
            });
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setLoading(false);
        return;
      }

      // Create mode: original flow
      // Create shipment first
      const response = await shipmentsAPI.create(submitData);
      const shipmentId = response.data.shipment?.id || response.data.shipmentId;

      // Upload files if any were selected
      if (selectedFiles.length > 0 && shipmentId) {
        try {
          // Upload all files
          const uploadPromises = selectedFiles.map(file => 
            uploadAPI.uploadFile(file, shipmentId)
          );
          await Promise.all(uploadPromises);
          
          setMessage({
            type: 'success',
            text: `Shipment and ${selectedFiles.length} file(s) uploaded successfully! Sending notification...`,
          });
        } catch (uploadErr) {
          console.error('File upload error:', uploadErr);
          setMessage({
            type: 'success',
            text: 'Shipment created but some files failed to upload. Sending notification...',
          });
        }
      } else {
        setMessage({
          type: 'success',
          text: 'Shipment created successfully! Sending notification...',
        });
      }

      // Send email notification after everything is persisted
      try {
        console.log(`[Shipment ${shipmentId}] Sending email notification...`);
        await shipmentsAPI.sendNotification(shipmentId);
        console.log(`[Shipment ${shipmentId}] Email notification sent successfully`);
        setMessage({
          type: 'success',
          text: 'Shipment created and notification sent! Redirecting to My Shipments...',
        });
      } catch (notifErr) {
        // Don't fail if notification fails, but log the error
        console.error(`[Shipment ${shipmentId}] Failed to send notification:`, notifErr);
        setMessage({
          type: 'warning',
          text: 'Shipment created but email notification failed. Please contact support. Redirecting to My Shipments...',
        });
      }

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Redirect after 3 seconds to give user time to see the message
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to create shipment',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="new-shipment" />

      <div className="content">
        <h1 className="page-title">{editMode ? 'Edit & Resubmit Shipment' : 'Domestic Shipment Entry'}</h1>

        {editMode && editRejectionData && (
          <RejectionBanner
            rejectionReason={editRejectionData.rejection_reason}
            rejectedByName={editRejectionData.rejected_by_name}
            rejectedAt={editRejectionData.rejected_at}
          />
        )}

        <div className="note-box">
          <strong>Note:</strong> All vendors with domestic shipping requests will be subject to a 24-hour
          review to verify potential savings. Questions on Shipping Portal Request should be directed to the Logistics Team -{' '}
          <a href="mailto:psmshipment@us.psm.com" className="contact-link">
            psmshipment@us.psm.com
          </a>
          .
        </div>

        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            {/* Shipper Section */}
            <div className="form-column">
              <h3 className="section-title">Shipper</h3>

              <AddressDropdown type="shipper" onSelect={handleShipperSelect} />

              <div className="form-field">
                <label>
                  Name <span className="required">*</span>
                </label>
                <div className="autocomplete-wrapper">
                  <input
                    type="text"
                    name="shipper_name"
                    value={formData.shipper_name}
                    onChange={handleInputChange}
                    required
                    list="shipper-names"
                    placeholder="Type or select from previous shippers..."
                  />
                  <span className="dropdown-icon">▼</span>
                  <datalist id="shipper-names">
                    {shipperSuggestions.map((name, idx) => (
                      <option key={idx} value={name} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="form-field">
                <label>
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_address"
                  value={formData.shipper_address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>Address 2</label>
                <input
                  type="text"
                  name="shipper_address2"
                  value={formData.shipper_address2}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_city"
                  value={formData.shipper_city}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>
                  State <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_state"
                  value={formData.shipper_state}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>
                  Zip <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_zip"
                  value={formData.shipper_zip}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>Country <span className="required">*</span></label>
                <select
                  name="shipper_country"
                  value={formData.shipper_country}
                  onChange={handleInputChange}
                  required
                >
                  <option value="US">US - United States</option>
                  <option value="CA">CA - Canada</option>
                  <option value="MX">MX - Mexico</option>
                  <option value="DE">DE - Germany</option>
                  <option value="GB">GB - United Kingdom</option>
                  <option value="FR">FR - France</option>
                  <option value="JP">JP - Japan</option>
                  <option value="CN">CN - China</option>
                  <option value="KR">KR - South Korea</option>
                  <option value="AU">AU - Australia</option>
                  <option value="IN">IN - India</option>
                  <option value="BR">BR - Brazil</option>
                  <option value="CH">CH - Switzerland</option>
                  <option value="IT">IT - Italy</option>
                  <option value="ES">ES - Spain</option>
                  <option value="NL">NL - Netherlands</option>
                  <option value="SE">SE - Sweden</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="form-field">
                <label>Contact <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_contact"
                  value={formData.shipper_contact}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Phone Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_phone"
                  value={formData.shipper_phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="shipper_email"
                  value={formData.shipper_email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Reference Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_reference_num"
                  value={formData.shipper_reference_num}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>IPT/ACP Num</label>
                <input
                  type="text"
                  name="shipper_ipt_acp_num"
                  value={formData.shipper_ipt_acp_num}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>NCR Num</label>
                <input
                  type="text"
                  name="shipper_ncr_num"
                  value={formData.shipper_ncr_num}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Purchase Order <span className="required">*</span></label>
                <input
                  type="text"
                  name="purchase_order"
                  value={formData.purchase_order}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Production Order <span className="required">*</span></label>
                <input
                  type="text"
                  name="production_order"
                  value={formData.production_order}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Charge Code <span className="required">*</span></label>
                <input
                  type="text"
                  name="charge_code"
                  value={formData.charge_code}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field checkbox-field">
                <label>
                  <input
                    type="checkbox"
                    name="ots"
                    checked={formData.ots}
                    onChange={handleInputChange}
                  />
                  OTS
                </label>
                <div className="field-note" style={{ color: '#d32f2f', fontWeight: '500' }}>
                  ***If OTS box is checked, WBS # must be supplied. COST CENTER, PO # not
                  acceptable.***
                </div>
              </div>

              {formData.ots && (
                <div className="form-field">
                  <label>WBS # *</label>
                  <input
                    type="text"
                    name="wbs_number"
                    value={formData.wbs_number}
                    onChange={handleInputChange}
                    required={formData.ots}
                    placeholder="Enter WBS number"
                  />
                </div>
              )}
            </div>

            {/* Consignee Section */}
            <div className="form-column">
              <h3 className="section-title">Consignee</h3>

              <AddressDropdown type="consignee" onSelect={handleConsigneeSelect} />

              <div className="form-field">
                <label>
                  Name <span className="required">*</span>
                </label>
                <div className="autocomplete-wrapper">
                  <input
                    type="text"
                    name="consignee_name"
                    value={formData.consignee_name}
                    onChange={handleInputChange}
                    required
                    list="consignee-names"
                    placeholder="Type or select from previous consignees..."
                  />
                  <span className="dropdown-icon">▼</span>
                  <datalist id="consignee-names">
                    {consigneeSuggestions.map((name, idx) => (
                      <option key={idx} value={name} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="form-field">
                <label>
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_address"
                  value={formData.consignee_address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>Address 2</label>
                <input
                  type="text"
                  name="consignee_address2"
                  value={formData.consignee_address2}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_city"
                  value={formData.consignee_city}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>
                  State <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_state"
                  value={formData.consignee_state}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>
                  Zip <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_zip"
                  value={formData.consignee_zip}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>Country <span className="required">*</span></label>
                <select
                  name="consignee_country"
                  value={formData.consignee_country}
                  onChange={handleInputChange}
                  required
                >
                  <option value="US">US - United States</option>
                  <option value="CA">CA - Canada</option>
                  <option value="MX">MX - Mexico</option>
                  <option value="DE">DE - Germany</option>
                  <option value="GB">GB - United Kingdom</option>
                  <option value="FR">FR - France</option>
                  <option value="JP">JP - Japan</option>
                  <option value="CN">CN - China</option>
                  <option value="KR">KR - South Korea</option>
                  <option value="AU">AU - Australia</option>
                  <option value="IN">IN - India</option>
                  <option value="BR">BR - Brazil</option>
                  <option value="CH">CH - Switzerland</option>
                  <option value="IT">IT - Italy</option>
                  <option value="ES">ES - Spain</option>
                  <option value="NL">NL - Netherlands</option>
                  <option value="SE">SE - Sweden</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="form-field">
                <label>Contact <span className="required">*</span></label>
                <input
                  type="text"
                  name="consignee_contact"
                  value={formData.consignee_contact}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Phone Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="consignee_phone"
                  value={formData.consignee_phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="consignee_email"
                  value={formData.consignee_email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Reference Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="consignee_reference_num"
                  value={formData.consignee_reference_num}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Pickup and Delivery */}
          <div className="form-row">
            <div className="form-column">
              <h3 className="section-title">Pickup</h3>
              <p className="note-text" style={{ fontSize: '0.85rem', color: '#856404', background: '#fff3cd', padding: '8px 12px', borderRadius: '4px', margin: '0 0 15px 0' }}>
                <strong>Note:</strong> All shipments entered after 2:00 PM EST will be processed the following business day.
              </p>

              <div className="form-field">
                <label>Pickup Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="pickup_date"
                  value={formData.pickup_date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>PU Window Start <span class="required">*</span></label>
                <input
                  type="time"
                  name="pickup_window_start"
                  value={formData.pickup_window_start}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>PU Window End <span class="required">*</span></label>
                <input
                  type="time"
                  name="pickup_window_end"
                  value={formData.pickup_window_end}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-column">
              <h3 className="section-title">Delivery</h3>

              <div className="form-field">
                <label>Delivery Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="delivery_date"
                  value={formData.delivery_date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>DEL Window Start <span class="required">*</span></label>
                <input
                  type="time"
                  name="delivery_window_start"
                  value={formData.delivery_window_start}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>DEL Window End <span class="required">*</span></label>
                <input
                  type="time"
                  name="delivery_window_end"
                  value={formData.delivery_window_end}
                  onChange={handleInputChange}
                />
              </div>

              <div className="field-note">
                Default for next day delivery timing will be afternoon as lowest cost option. Please
                specify a morning delivery time only if required in special instructions.
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="form-section">
            <h3 className="section-title">Bill To</h3>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="bill_to"
                  value="inpayer"
                  checked={formData.bill_to === 'inpayer'}
                  onChange={handleInputChange}
                />
                Prepaid
              </label>
              <label>
                <input
                  type="radio"
                  name="bill_to"
                  value="collect"
                  checked={formData.bill_to === 'collect'}
                  onChange={handleInputChange}
                />
                Collect
              </label>
              <label>
                <input
                  type="radio"
                  name="bill_to"
                  value="third_party"
                  checked={formData.bill_to === 'third_party'}
                  onChange={handleInputChange}
                />
                Third Party
              </label>
            </div>
          </div>

          {/* Shipping */}
          <div className="form-section">
            <h3 className="section-title">Shipping</h3>

            <div className="form-field" style={{ marginBottom: '20px' }}>
              <label>Is this an Active Outage? <span className="required">*</span></label>
              <div className="radio-group">
                <label className="radio-label">
                  <input type="radio" name="active_outage" value="true" checked={formData.active_outage === 'true'} onChange={handleInputChange} />
                  Yes
                </label>
                <label className="radio-label">
                  <input type="radio" name="active_outage" value="false" checked={formData.active_outage === 'false'} onChange={handleInputChange} />
                  No
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Service <span className="required">*</span></label>
                <select name="service" value={formData.service} onChange={handleInputChange}>
                  <option value="">Select...</option>
                  <option value="standard">Standard</option>
                  <option value="expedite">Expedite</option>
                </select>
              </div>

              <div className="form-field">
                <label>Expedite</label>
                <select name="expedite" value={formData.expedite} onChange={handleInputChange}>
                  <option value="">Select...</option>
                  <option value="1_day">1 Day</option>
                  <option value="2_day">2 Day</option>
                </select>
              </div>

              <div className="form-field">
                <label>First Approval</label>
                <select
                  name="first_approval"
                  value={formData.first_approval}
                  onChange={handleInputChange}
                >
                  <option value="">Select...</option>
                  <option value="Christopher Johnston">Christopher Johnston</option>
                  <option value="Henry Knight">Henry Knight</option>
                  <option value="Dustin Paxton">Dustin Paxton</option>
                </select>
              </div>

              <div className="form-field">
                <label>Second Approval</label>
                <select
                  name="second_approval"
                  value={formData.second_approval}
                  onChange={handleInputChange}
                >
                  <option value="">Select...</option>
                  <option value="Christopher Johnston">Christopher Johnston</option>
                  <option value="Henry Knight">Henry Knight</option>
                  <option value="Dustin Paxton">Dustin Paxton</option>
                </select>
              </div>
            </div>
            <div className="field-note">***EXPEDITED SHIPMENTS MUST HAVE 2 APPROVALS***</div>
          </div>

          {/* Accessorials */}
          <div className="form-section">
            <h3 className="section-title">Accessorials</h3>
            <div className="accessorials-grid">
              <div className="accessorial-item">
                <span>Liftgate</span>
                <label>
                  <input
                    type="checkbox"
                    name="acc_liftgate_air"
                    checked={formData.acc_liftgate_air}
                    onChange={handleInputChange}
                  />
                  Air
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="acc_liftgate_gr"
                    checked={formData.acc_liftgate_gr}
                    onChange={handleInputChange}
                  />
                  GR
                </label>
              </div>

              <div className="accessorial-item">
                <span>Inside</span>
                <label>
                  <input
                    type="checkbox"
                    name="acc_inside_air"
                    checked={formData.acc_inside_air}
                    onChange={handleInputChange}
                  />
                  Air
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="acc_inside_gr"
                    checked={formData.acc_inside_gr}
                    onChange={handleInputChange}
                  />
                  GR
                </label>
              </div>

              <div className="accessorial-item">
                <span>Rigging Required</span>
                <label>
                  <input
                    type="checkbox"
                    name="acc_rigging_air"
                    checked={formData.acc_rigging_air}
                    onChange={handleInputChange}
                  />
                  Air
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="acc_rigging_gr"
                    checked={formData.acc_rigging_gr}
                    onChange={handleInputChange}
                  />
                  GR
                </label>
              </div>

              <div className="accessorial-item">
                <span>Construction Site</span>
                <label>
                  <input
                    type="checkbox"
                    name="acc_construction_air"
                    checked={formData.acc_construction_air}
                    onChange={handleInputChange}
                  />
                  Air
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="acc_construction_gr"
                    checked={formData.acc_construction_gr}
                    onChange={handleInputChange}
                  />
                  GR
                </label>
              </div>

              <div className="accessorial-item">
                <span>Tarp Needed</span>
                <label>
                  <input
                    type="checkbox"
                    name="acc_tarp_needed"
                    checked={formData.acc_tarp_needed}
                    onChange={handleInputChange}
                  />
                  Yes
                </label>
              </div>

              <div className="accessorial-item">
                <span>Protect From Freezing</span>
                <label>
                  <input
                    type="checkbox"
                    name="acc_protect_freezing"
                    checked={formData.acc_protect_freezing}
                    onChange={handleInputChange}
                  />
                  Yes
                </label>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="form-section">
            <h3 className="section-title">Line Items <span style={{ color: '#dc3545' }}>*</span></h3>
            <div className="field-note" style={{ color: '#dc3545', fontWeight: 'bold' }}>
              ⚠️ REQUIRED: At least one line item with Pieces (PCS), Type, and Weight must be added
            </div>
            <div className="field-note">
              ***For all shipments leaving a PSM location, Logistics will provide weights &
              dimensions.***
            </div>
            <div className="field-note" style={{ marginTop: '10px', color: '#0066cc' }}>
              Click 'Update' to lock in added entry
            </div>

            <div className="line-items-container">
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Handling Units</th>
                    <th>Type</th>
                    <th>Weight (lbs)</th>
                    <th>Length (in)</th>
                    <th>Width (in)</th>
                    <th>Height (in)</th>
                    <th>Description</th>
                    <th>Class</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="no-data">
                        No data to display
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((item, index) => (
                      <tr key={index} className={item.locked ? 'locked-row' : ''}>
                        <td>{index + 1}</td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.handling_units}</span>
                          ) : (
                            <input
                              type="text"
                              value={item.handling_units}
                              onChange={(e) =>
                                updateLineItem(index, 'handling_units', e.target.value)
                              }
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.type}</span>
                          ) : (
                            <select
                              value={item.type}
                              onChange={(e) => updateLineItem(index, 'type', e.target.value)}
                            >
                              <option value="">Select...</option>
                              <option value="Pallets">Pallets</option>
                              <option value="Pieces">Pieces</option>
                              <option value="Crates">Crates</option>
                              <option value="Cases">Cases</option>
                              <option value="Boxes">Boxes</option>
                            </select>
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.weight_lbs}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.weight_lbs}
                              onChange={(e) => updateLineItem(index, 'weight_lbs', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.length_in}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.length_in}
                              onChange={(e) => updateLineItem(index, 'length_in', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.width_in}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.width_in}
                              onChange={(e) => updateLineItem(index, 'width_in', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.height_in}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.height_in}
                              onChange={(e) => updateLineItem(index, 'height_in', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.description}</span>
                          ) : (
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.class}</span>
                          ) : (
                            <select
                              value={item.class}
                              onChange={(e) => updateLineItem(index, 'class', e.target.value)}
                            >
                              <option value="">Select...</option>
                              <option value="50">Class 50</option>
                              <option value="55">Class 55</option>
                              <option value="60">Class 60</option>
                              <option value="65">Class 65</option>
                              <option value="70">Class 70</option>
                              <option value="77.5">Class 77.5</option>
                              <option value="85">Class 85</option>
                              <option value="92.5">Class 92.5</option>
                              <option value="100">Class 100</option>
                              <option value="110">Class 110</option>
                              <option value="125">Class 125</option>
                              <option value="150">Class 150</option>
                              <option value="175">Class 175</option>
                              <option value="200">Class 200</option>
                              <option value="250">Class 250</option>
                              <option value="300">Class 300</option>
                              <option value="400">Class 400</option>
                              <option value="500">Class 500</option>
                            </select>
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <>
                              <button
                                type="button"
                                className="edit-button"
                                onClick={() => unlockLineItem(index)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="remove-button"
                                onClick={() => removeLineItem(index)}
                              >
                                Remove
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="update-button"
                                onClick={() => lockLineItem(index)}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="cancel-button"
                                onClick={() => removeLineItem(index)}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <button type="button" className="add-button" onClick={addLineItem}>
                New
              </button>
            </div>
          </div>

          {/* Product Location */}
          <div className="form-section">
            <h3 className="section-title">Product Location</h3>
            <div className="form-field">
              <select
                name="product_location"
                value={formData.product_location}
                onChange={handleInputChange}
              >
                <option value="">Select...</option>
                <option value="New Make">New Make</option>
                <option value="Ordered from EWMS">Ordered from EWMS</option>
                <option value="RECO Packing">RECO Packing</option>
                <option value="Shipping Department">Shipping Department</option>
                <option value="Vendor Location">Vendor Location</option>
                <option value="Ventex">Ventex</option>
                <option value="Will Deliver to Shipping">Will Deliver to Shipping</option>
                <option value="WPB Warehouse">WPB Warehouse</option>
              </select>
            </div>
          </div>

          {/* File Attachment */}
          <div className="form-section">
            <h3 className="section-title">File Attachments (Max 10)</h3>
            <div className="form-field">
              <input
                type="file"
                name="file_attachment"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                multiple
              />
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ color: '#4CAF50', fontSize: '13px', marginBottom: '5px' }}>
                    {selectedFiles.length} file(s) selected:
                  </div>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                      • {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </div>
                  ))}
                </div>
              )}
              <div className="field-note">
                Browse and select up to 10 files from your local computer (PDF, DOC, DOCX, XLS, XLSX, JPG,
                PNG - Max 10MB per file)
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          <div className="form-section">
            <h3 className="section-title">Special Instructions</h3>
            <div className="form-field">
              <textarea
                name="special_instructions"
                value={formData.special_instructions}
                onChange={handleInputChange}
                rows="5"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="clear-button"
              onClick={handleClearForm}
              disabled={loading}
            >
              Clear Form
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewShipment;

